/**
 * The canonical performance metric block for the consolidated surface (#6039, T1).
 *
 * Every unified table derives the same three layers from the same raw components,
 * so ROAS / CPC / CTR / conversion-rates are computed once here rather than
 * re-implemented (inconsistently) per table:
 *   Layer 1 — spend · ROAS · CPC
 *   Layer 2 — impressions · clicks · results        (raw passthroughs)
 *   Layer 3 — CTR · impression→conversion · click→conversion
 *
 * Rates are returned as fractions (0–1); a metric is `null` when its denominator
 * is zero or its input is untracked (e.g. Meta has no conversion value yet, so
 * ROAS is null → "—" until #6044). Money is expected already in display units
 * (see money.ts); ROAS is unitless and CPC stays in display currency.
 */

const NO_VALUE = '—';

export interface MetricInputs {
  impressions: number;
  clicks: number;
  /** Conversions (Google) or leads (Meta). */
  results: number;
  /** Spend in display currency (already normalized — see money.toDisplayAmount). */
  spend: number;
  /** Conversion value in display currency; null/undefined when untracked → ROAS unavailable. */
  conversionValue?: number | null;
}

export interface DerivedMetrics {
  /** ROAS = conversionValue / spend. Null when value untracked or no spend. */
  roas: number | null;
  /** CPC = spend / clicks (display currency). */
  cpc: number | null;
  /** CTR = clicks / impressions (fraction). */
  ctr: number | null;
  /** Impression→conversion rate = results / impressions (fraction). */
  imprToConv: number | null;
  /** Click→conversion rate (conversion rate) = results / clicks (fraction). */
  clickToConv: number | null;
}

export function deriveMetrics(m: MetricInputs): DerivedMetrics {
  const { impressions, clicks, results, spend } = m;
  const value = m.conversionValue;
  return {
    // Require value > 0: a zero conversion-value means the account tracks no
    // revenue, so ROAS reads "—" rather than a misleading 0.0x.
    roas: value != null && Number.isFinite(value) && value > 0 && spend > 0 ? value / spend : null,
    cpc: clicks > 0 ? spend / clicks : null,
    ctr: impressions > 0 ? clicks / impressions : null,
    imprToConv: impressions > 0 ? results / impressions : null,
    clickToConv: clicks > 0 ? results / clicks : null,
  };
}

/** Format a rate fraction as a percentage string; "—" for null / non-finite. */
export function formatRate(rate: number | null | undefined, digits = 2): string {
  if (rate == null || !Number.isFinite(rate)) return NO_VALUE;
  return `${(rate * 100).toFixed(digits)}%`;
}

/** Format ROAS as e.g. "3.2x"; "—" for null / non-finite. */
export function formatRoas(roas: number | null | undefined): string {
  if (roas == null || !Number.isFinite(roas)) return NO_VALUE;
  return `${roas.toFixed(1)}x`;
}
